import React from 'react';
import Skeleton from 'src/components/Skeleton/Skeleton';

const SUITE_TABLE_ROW_COUNT: number = 6;

// Mirrors BulkAddWalkthroughs form layout (BulkAddWalkthroughs.tsx):
// Name input, Permissions + Walkthrough Type radio groups side-by-side,
// Description textarea, Choose-suites label + scrollable suite table, then
// the bottom-action bar with the primary save button.
const BulkAddWalkthroughsDialogueSkeleton = () => (
  <div
    style={{
      minHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      padding: '0.5rem 0.25rem',
    }}
  >
    {/* 1. Name field. */}
    <div style={{ marginBottom: '1.25rem' }}>
      <div style={{ marginBottom: '0.5rem' }}>
        <Skeleton variant="text" width="4rem" />
      </div>
      <Skeleton variant="rect" width="100%" height="2.5rem" />
    </div>

    {/* 2. Permissions + Walkthrough Type radio groups (side-by-side). */}
    <div
      style={{
        display: 'flex',
        gap: '2.5rem',
        marginBottom: '1.25rem',
        flexWrap: 'wrap',
      }}
    >
      <RadioGroupSkeleton labelWidth="6rem" />
      <RadioGroupSkeleton labelWidth="9rem" />
    </div>

    {/* 3. Description textarea. */}
    <div style={{ marginBottom: '1.25rem' }}>
      <div style={{ marginBottom: '0.5rem' }}>
        <Skeleton variant="text" width="6rem" />
      </div>
      <Skeleton variant="rect" width="100%" height="5rem" />
    </div>

    {/* 4. Choose-suites label + suite table. */}
    <div style={{ marginBottom: '1.25rem', flex: 1 }}>
      <div style={{ marginBottom: '0.5rem' }}>
        <Skeleton variant="text" width="18rem" />
      </div>
      <SuiteTableSkeleton />
    </div>

    {/* 5. Footer save button. */}
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: 'auto',
        paddingTop: '1.5rem',
      }}
    >
      <Skeleton variant="rect" width="13rem" height="2.5rem" />
    </div>
  </div>
);

interface RadioGroupSkeletonProps {
  labelWidth: string;
}

const RadioGroupSkeleton = ({ labelWidth }: RadioGroupSkeletonProps) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
    <Skeleton variant="text" width={labelWidth} />
    <div style={{ display: 'flex', gap: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Skeleton variant="circle" width="1rem" height="1rem" />
        <Skeleton variant="text" width="5rem" />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Skeleton variant="circle" width="1rem" height="1rem" />
        <Skeleton variant="text" width="9rem" />
      </div>
    </div>
  </div>
);

const SuiteTableSkeleton = () => (
  <div style={{ border: '1px solid #e3e3e6', borderRadius: '4px' }}>
    {/* Header row. */}
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '2rem 1fr 1fr 1fr 1fr',
        gap: '1rem',
        padding: '1rem 1.5rem',
        borderBottom: '1px solid #d6d6d6',
      }}
    >
      <Skeleton variant="rect" width="1rem" height="1rem" />
      <Skeleton variant="text" width="60%" />
      <Skeleton variant="text" width="60%" />
      <Skeleton variant="text" width="60%" />
      <Skeleton variant="text" width="60%" />
    </div>

    {/* Body rows. */}
    {Array.from({ length: SUITE_TABLE_ROW_COUNT }, (_, index: number) => (
      <div
        key={`bulk-add-suite-row-${index}`}
        style={{
          display: 'grid',
          gridTemplateColumns: '2rem 1fr 1fr 1fr 1fr',
          gap: '1rem',
          padding: '1rem 1.5rem',
          borderTop: index === 0 ? 'none' : '1px solid #d6d6d6',
        }}
      >
        <Skeleton variant="rect" width="1rem" height="1rem" />
        <Skeleton variant="text" width="70%" />
        <Skeleton variant="text" width="70%" />
        <Skeleton variant="text" width="70%" />
        <Skeleton variant="text" width="70%" />
      </div>
    ))}
  </div>
);

export default BulkAddWalkthroughsDialogueSkeleton;
